import { AlertTriangle, Bot, Info, KeyRound, LogOut, Palette } from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import { useKaannos } from '../lib/i18n';
import { useTrainingAlerts } from '../lib/queries';
import { useLogout, useSession } from '../lib/session';
import Dialogi from './Dialogi';
import Logo from './Logo';
import SalasananVaihto from './SalasananVaihto';
import SovellusMerkki from './SovellusMerkki';

interface NavItem {
  to: string;
  label: string;
  visible: boolean;
}

export default function AppShell() {
  const { user, permissions, hasLocalLogin } = useSession();
  const logout = useLogout();
  const { t } = useKaannos();
  const [salasanaAuki, setSalasanaAuki] = useState(false);
  const { data: koulutusTilanne } = useTrainingAlerts();

  const items: NavItem[] = [
    { to: '/kalenteri', label: t('navi.kalenteri'), visible: true },
    { to: '/omat-tunnit', label: t('navi.omatTunnit'), visible: true },
    { to: '/koulutukset', label: t('navi.koulutukset'), visible: true },
    { to: '/kehitys', label: t('navi.kehitys'), visible: permissions.canUseDevelopment },
    { to: '/taulut', label: t('navi.taulut'), visible: permissions.canUseBoards },
    { to: '/raportit', label: t('navi.raportit'), visible: permissions.canViewReports },
    { to: '/vienti', label: t('navi.vienti'), visible: permissions.canExport },
    { to: '/salaisuudet', label: t('navi.salaisuudet'), visible: permissions.canShareSecrets },
    {
      to: '/hallinta/asiakkaat',
      label: t('navi.hallinta'),
      visible: permissions.canManageMasterData,
    },
    // Raportoija ja asiakasvastaava pääsevät kuukausinäkymään suoraan;
    // ADMINille se on hallinnan välilehti eikä kaipaa omaa linkkiä (6.5).
    {
      to: '/hallinta/kuukaudet',
      label: t('navi.kuukaudet'),
      visible: permissions.canViewReports && !permissions.canManageMasterData,
    },
  ];

  return (
    <div className="min-h-dvh">
      <header className="border-reuna bg-pinta-korotettu sticky top-0 z-10 border-b">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
          {/* Yrityksen tunnus ja sovelluksen nimi ovat eri asioita: tunnus
            kertoo kenen järjestelmä tämä on, nimi mikä järjestelmä se on. */}
          <div className="flex items-center gap-2.5">
            <Logo className="h-7 w-auto max-w-36 object-contain" />
            <span className="bg-reuna h-5 w-px shrink-0" aria-hidden="true" />
            <SovellusMerkki className="text-teksti size-6 shrink-0" />
            <span className="text-teksti font-semibold tracking-tight">{t('sovellus.nimi')}</span>
          </div>

          <nav aria-label={t('navi.paanavigaatio')} className="flex flex-wrap items-center gap-1">
            {items
              .filter((item) => item.visible)
              .map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    [
                      'relative rounded-md py-1.5 pl-4 pr-3 text-sm transition-colors',
                      isActive
                        ? 'text-teksti font-medium'
                        : 'text-teksti-vaimea hover:text-teksti hover:bg-pinta',
                    ].join(' ')
                  }
                >
                  {/* Valittu näkymä merkitään punaisella pisteellä, samalla
                    kuin sanamerkin i:n piste ja esittelyn yläotsikot. Piste
                    istuu linkin vasemmassa sisennyksessä eikä vie leveyttä,
                    joten valinnan vaihtuminen ei siirrä rivin linkkejä.
                    Valinnan kertoo ruudunlukijalle NavLinkin aria-current,
                    ei piste. */}
                  {({ isActive }) => (
                    <>
                      <span
                        aria-hidden="true"
                        className={[
                          'absolute left-1.5 top-1/2 size-1.5 -translate-y-1/2 rounded-full transition-colors',
                          isActive ? 'bg-brandi' : 'bg-transparent',
                        ].join(' ')}
                      />
                      {item.label}
                    </>
                  )}
                </NavLink>
              ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <div className="text-right leading-tight">
              <div className="text-teksti text-sm font-medium">{user.name}</div>
              <div className="text-teksti-vaimea text-xs">{t(`rooli.${user.role}`)}</div>
            </div>
            {/* Agenttitunnus on kuvakepainike eikä navigaatiolinkki: sitä
              käydään asettamassa kerran, eikä se ansaitse paikkaa niiden
              näkymien rinnalla, joita avataan päivittäin (backlog 5). */}
            {permissions.canLogTime && (
              <Link
                to="/agentti"
                aria-label={t('navi.agentti')}
                title={t('navi.agentti')}
                className="border-reuna text-teksti-vaimea hover:text-teksti hover:border-reuna-vahva rounded-md border p-2 transition-colors"
              >
                <Bot className="size-4" aria-hidden="true" />
              </Link>
            )}
            {/* Ulkoasu on jokaisen omat asetukset, ei hallinnan välilehti:
              teeman, kielen ja asiakasvärien pitää olla myös sen kirjaajan
              ulottuvilla, joka ei pääse hallintaan lainkaan. Kuvakepainike
              agenttitunnuksen tapaan — sivu avataan kerran, eikä se kuulu
              päivittäin avattavien näkymien riviin. */}
            <Link
              to="/ulkoasu"
              aria-label={t('navi.ulkoasu')}
              title={t('navi.ulkoasu')}
              className="border-reuna text-teksti-vaimea hover:text-teksti hover:border-reuna-vahva rounded-md border p-2 transition-colors"
            >
              <Palette className="size-4" aria-hidden="true" />
            </Link>
            {/* Vain paikallisella tilillä on salasana, jonka voi vaihtaa.
              Microsoft-tilin salasana vaihdetaan Microsoftilla. */}
            {hasLocalLogin && (
              <button
                type="button"
                onClick={() => setSalasanaAuki(true)}
                aria-label={t('salasana.vaihda')}
                title={t('salasana.vaihda')}
                className="border-reuna text-teksti-vaimea hover:text-teksti hover:border-reuna-vahva rounded-md border p-2 transition-colors"
              >
                <KeyRound className="size-4" aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              onClick={() => void logout()}
              className="border-reuna text-teksti-vaimea hover:text-teksti hover:border-reuna-vahva flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors"
            >
              <LogOut className="size-4" aria-hidden="true" />
              {t('navi.kirjauduUlos')}
            </button>
          </div>
        </div>

        {/**
         * Puuttuva pakollinen koulutus on palkki eikä merkki navigaatiossa.
         *
         * Merkki kertoisi, että jotain on, muttei mitä eikä miksi — ja
         * compliance-puute on tarkalleen se asia, jonka pitää sanoa itsensä
         * ääneen. Palkki näkyy vain silloin kun on jotain kerrottavaa, joten
         * se ei ehdi muuttua taustakuvioksi.
         */}
        {koulutusTilanne && koulutusTilanne.overdue > 0 && (
          <div className="border-yli/30 bg-yli/10 border-t px-4 py-2">
            <p className="text-teksti mx-auto flex max-w-7xl items-start gap-1.5 text-xs">
              <AlertTriangle className="text-yli mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              <span>
                {t('koulutus.varoitusPuuttuu', koulutusTilanne.overdue)}{' '}
                <Link to="/koulutukset" className="text-korostus underline underline-offset-2">
                  {t('koulutus.varoitusLinkki')}
                </Link>
              </span>
            </p>
          </div>
        )}

        {koulutusTilanne && koulutusTilanne.overdue === 0 && koulutusTilanne.expiring > 0 && (
          <div className="border-varoitus/30 bg-varoitus/10 border-t px-4 py-2">
            <p className="text-teksti mx-auto flex max-w-7xl items-start gap-1.5 text-xs">
              <AlertTriangle
                className="text-varoitus mt-0.5 size-3.5 shrink-0"
                aria-hidden="true"
              />
              <span>
                {t('koulutus.varoitusVanhenee', koulutusTilanne.expiring)}{' '}
                <Link to="/koulutukset" className="text-korostus underline underline-offset-2">
                  {t('koulutus.varoitusLinkki')}
                </Link>
              </span>
            </p>
          </div>
        )}

        {user.role === 'ACCOUNT_MANAGER' && user.assignedCustomers.length > 0 && (
          <div className="border-reuna bg-korostus-pehmea/40 border-t px-4 py-2">
            <p className="text-teksti-vaimea mx-auto flex max-w-7xl items-start gap-1.5 text-xs">
              <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              <span>
                {t('kehys.vastuuasiakkaasi')}:{' '}
                <span className="text-teksti font-medium">
                  {user.assignedCustomers.map((customer) => customer.name).join(', ')}
                </span>
                . {t('kehys.vastuuasiakkaatSelite')}
              </span>
            </p>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>

      <Dialogi
        otsikko={t('salasana.vaihda')}
        auki={salasanaAuki}
        onSulje={() => setSalasanaAuki(false)}
      >
        <SalasananVaihto
          onValmis={() => setSalasanaAuki(false)}
          onPeruuta={() => setSalasanaAuki(false)}
        />
      </Dialogi>
    </div>
  );
}
